import {Fragment, useEffect, useState} from 'react';
import Markdown from 'react-markdown';

type Agreement = {
  id: string;
  label: string;
  link?: {
    text: string;
    url: string;
  };
};

type ContractComponentProps = {
  markdown?: string;
  useLinks?: boolean;
  agreements?: Agreement[];
  onAccept?: () => void;
  onDecline?: () => void;
  title?: string;
};

const defaultMarkdown = `
# YNEX - Terms & Conditions

## For Users in the USA

The YNEX [Terms and Conditions](/#) consists of the rules below and [User Agreements](/#) consists of the following policies [YNEX Rules & Privacy Policies](/#) incorporated with the below conditions.

## For Users Outside the USA

The YNEX [Terms and Conditions](/#) consists of the rules below and [User Agreements](/#) consists of the following policies [YNEX Rules & Privacy Policies](/#) incorporated with the below conditions.

## Terms & Services:

### 1. Lorem ipsum dolor sit amet.

Note that you'll sometimes see this agreement referred to as a Terms of Use, User Agreement or Terms of Service agreement. These terms are interchangeable and refer to the same type of agreement.

### 2. Consectetur adipisicing elit.

While they are not legally required, terms and conditions set the stage for any successful business relationship. By making it clear and putting these guidelines in writing, business owners can avoid misunderstandings with their customers.

It also allows you to decide what you consider acceptable and which type of conduct could lead you to terminate a relationship with a user.

### 3. There are many variations.

Limitation of liability disclaimers is one of the main reasons why business owners take the time to include terms and conditions on their websites. When reasonable and drafted adequately, such clauses can help protect your business against claims and lawsuits and limit the amount of money that you would have to pay in damages.

### 4. If you allow your users to share.

If you allow your users to share comments or photos on your website or leave reviews of the products that you sell, you will want to have a section in your terms that governs their conduct and sets out what is acceptable and what isn't.

In this clause, you could reserve the right to monitor the user-generated content shared on your website and remove anything that goes against your guidelines. You could expressly ask your users not to post anything that contains obscene language or any material that could be considered harmful or violent or infringes on someone else's copyright.

### 5. You could also make it clear.

You could also make it clear that you reserve the right to suspend or delete the accounts of repeat infringers. This will help you make your website a safe space where people can feel comfortable sharing their opinions, which is especially important if you operate a news site, blog, or forum.

From a business point of view, you could reserve the right to use the submitted content for marketing purposes which a lot of big box stores and eCommerce retailers do in order to promote products that get rave reviews. It's important for your customers to know that you plan on doing so, otherwise they could be surprised to see their words or photo used in a commercial!
`;

const MarkdownLink = ({
  href,
  children,
  useLinks = true,
  ...props
}: React.HTMLProps<HTMLAnchorElement> & {useLinks?: boolean}) => {
  if (!useLinks) {
    return <span className="text-primary">{children}</span>;
  }

  return (
    <a className="text-primary" href={href} {...props}>
      <u>{children}</u>
    </a>
  );
};

function ContractComponent({
  markdown = defaultMarkdown,
  title = 'YNEX - Terms & Conditions',
  useLinks = false,
  agreements = [],
  onAccept,
  onDecline,
}: ContractComponentProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedAgreements, setSelectedAgreements] = useState<Record<string, boolean>>({});

  // Check if all agreements are selected
  const areAllAgreementsSelected =
    agreements.length > 0 && agreements.every(agreement => selectedAgreements[agreement.id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    const contentContainer = document.getElementById('content-container');

    if (!contentContainer) {
      console.error("Element with ID 'content-container' not found in the DOM.");
      return;
    }

    if (!isFullscreen) {
      contentContainer.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleAgreementChange = (agreementId: string, checked: boolean) => {
    setSelectedAgreements(prev => ({
      ...prev,
      [agreementId]: checked,
    }));
  };

  const handleAccept = () => {
    if (areAllAgreementsSelected) {
      onAccept?.();
    }
  };

  return (
    <Fragment>
      <div className="container">
        <div className="!mx-auto grid grid-cols-12 text-defaultsize">
          <div className="col-span-12 xl:col-span-2"></div>
          <div className="col-span-12 xl:col-span-8">
            <div
              className={
                isFullscreen ? 'box terms-box box-fullscreen overflow-hidden' : 'box terms-box overflow-hidden'
              }
              id="content-container"
            >
              <div className="box-body !p-0">
                <div className="terms-heading-cover h5 mb-0 flex items-center bg-primary p-4 font-semibold text-white">
                  <span className="avatar avatar-md me-4">
                    <img src={'/images/media/media-1.jpg'} alt="" />
                  </span>
                  <span>{title}</span>
                  <a
                    aria-label="anchor"
                    href="#"
                    className="terms-fullscreen ms-auto text-white"
                    onClick={toggleFullscreen}
                  >
                    <i className="ri-fullscreen-line"></i>
                  </a>
                </div>
                <div id="terms-scroll" className="terms-conditions overflow-y-scroll">
                  <div className="text-muted p-6">
                    {/* @ts-expect-error -  Markdown types is incorrect */}
                    <Markdown
                      components={{
                        // @ts-expect-error -  Markdown types is incorrect
                        h1: ({children, ...props}: React.HTMLProps<HTMLHeadingElement>) => (
                          <h1 className="text-default mb-4 text-2xl font-bold" {...props}>
                            {children}
                          </h1>
                        ),
                        // @ts-expect-error -  Markdown types is incorrect
                        h2: ({children, ...props}: React.HTMLProps<HTMLHeadingElement>) => (
                          <h2 className="text-default mb-3 text-xl font-bold" {...props}>
                            {children}
                          </h2>
                        ),
                        // @ts-expect-error -  Markdown types is incorrect
                        h3: ({children, ...props}: React.HTMLProps<HTMLHeadingElement>) => (
                          <h3 className="text-muted mb-2 text-[.875rem] font-semibold opacity-[0.7]" {...props}>
                            {children}
                          </h3>
                        ),
                        // @ts-expect-error -  Markdown types is incorrect
                        p: ({children, ...props}: React.HTMLProps<HTMLParagraphElement>) => (
                          <p className="mb-4 opacity-[0.7]" {...props}>
                            {children}
                          </p>
                        ),
                        // @ts-expect-error -  Markdown types is incorrect
                        a: ({children, href, ...props}: React.HTMLProps<HTMLAnchorElement>) => (
                          <MarkdownLink href={href} useLinks={useLinks} {...props}>
                            {children}
                          </MarkdownLink>
                        ),
                      }}
                    >
                      {markdown}
                    </Markdown>
                  </div>
                </div>
              </div>
              <div className="box-footer block items-center justify-between !border-t-0 shadow-lg md:flex">
                <div>
                  {agreements.map(agreement => (
                    <div key={agreement.id} className="form-check !flex !ps-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={agreement.id}
                        checked={selectedAgreements[agreement.id] || false}
                        onChange={e => handleAgreementChange(agreement.id, e.target.checked)}
                      />
                      <label
                        className="form-check-label inline-block ps-3 text-defaulttextcolor/70"
                        htmlFor={agreement.id}
                      >
                        {agreement.label}{' '}
                        {agreement.link && (
                          <a href={agreement.link.url} className="font-semibold text-defaulttextcolor/70">
                            <u>{agreement.link.text}</u>
                          </a>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="btn-list mt-2 space-x-2 sm:mt-0 rtl:space-x-reverse">
                  <button type="button" className="ti-btn ti-btn-outline-danger" onClick={onDecline}>
                    DECLINE
                  </button>
                  <button
                    type="button"
                    className={`ti-btn ${
                      areAllAgreementsSelected ? 'ti-btn-primary-full' : 'cursor-not-allowed bg-gray-400'
                    }`}
                    disabled={!areAllAgreementsSelected}
                    onClick={handleAccept}
                  >
                    ACCEPT
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-12 xl:col-span-2"></div>
        </div>
      </div>
    </Fragment>
  );
}

export default ContractComponent;
